import * as React from "react";
import {useTranslation} from "react-i18next";
import {CheckoutStep} from "./Checkout";
import {useCheckoutViewContext} from "./CheckoutContextProvider/CheckoutViewContextProvider";
import {useEffect, useState} from "react";
import styles from "./Breadcrumb.module.scss";
import {useCustomerSelector} from "../../store";

type CrumbProps = {
  title: string;
  number: number;
  view: CheckoutStep;
  state: BreadcrumbState;
  disabled: boolean;
};

enum BreadcrumbState {
  ACTIVE = "active",
  DONE = "done",
  PENDING = "pending",
}

const Breadcrumb: React.FC = () => {
  const checkoutSteps = [
    {title: "customer.logIn", step: CheckoutStep.LOGIN},
    {title: "form.address", step: CheckoutStep.ADDRESS},
    {title: "general.shipping", step: CheckoutStep.SHIPPING},
    {title: "checkout.payment", step: CheckoutStep.PAYMENT},
    {title: "general.check", step: CheckoutStep.SUMMARY},
  ];
  const {t} = useTranslation();
  const checkoutView = useCheckoutViewContext();
  const {gotoStep, step} = checkoutView;
  const {customer} = useCustomerSelector();
  const stepIndex = checkoutSteps.findIndex((checkoutStep) => checkoutStep.step === step);
  const [touchedSteps, setTouchedSteps] = useState<Set<CheckoutStep>>(new Set());

  useEffect(() => {
    setTouchedSteps((prevState) => {
      checkoutSteps.forEach((checkoutStep, index) => {
        if (index <= stepIndex && index !== 4) {
          prevState.add(checkoutStep.step);
        }
      });

      return prevState;
    });
  }, [step]);

  // init touched steps
  useEffect(() => {
    const touchedStepsSet = new Set<CheckoutStep>();
    checkoutSteps.forEach((checkoutStep, index) => {
      if (index <= stepIndex && index !== 4) {
        touchedStepsSet.add(checkoutStep.step);
      }
    });
    setTouchedSteps(touchedStepsSet);
  }, []);

  const Crumb: React.FC<CrumbProps> = ({title, number, state, view, disabled}) => {
    return (
      <button
        type="button"
        disabled={disabled || !touchedSteps.has(view)}
        className={`${styles.crumbWrapper} ${styles[state]} ${disabled ? styles.inactive : ""}`}
        onClick={() => {
          if (!disabled && touchedSteps.has(view)) {
            gotoStep(view);
          }
        }}
      >
        <span className={styles.separator} />
        <div className={styles.step}>
          <span className={styles.step__number}>{number}</span>
          <span title={t(title)} className={styles.step__title}>
            {t(title)}
          </span>
        </div>
      </button>
    );
  };

  return (
    <div className={`${styles.breadCrumbWrapper} reduced-width`}>
      {checkoutSteps.map((checkoutStep, index) => {
        const breadcrumbDisabled = checkoutStep.step === CheckoutStep.LOGIN && customer.isLoggedIn;

        let state: BreadcrumbState;
        if (stepIndex === index) {
          state = BreadcrumbState.ACTIVE;
        } else if (stepIndex > index || touchedSteps.has(checkoutStep.step)) {
          state = BreadcrumbState.DONE;
        } else {
          state = BreadcrumbState.PENDING;
        }

        return (
          <Crumb
            key={index}
            disabled={breadcrumbDisabled}
            title={checkoutStep.title}
            view={checkoutStep.step}
            state={state}
            number={index + 1}
          />
        );
      })}
    </div>
  );
};

export default Breadcrumb;
